/**
 * Indigo NavbarTab component for sidebar navigation.
 * Renders a navigation tab, external link, or separator for the Indigo Navbar.
 *
 * Features:
 * - Internal navigation via NavLink
 * - External links open in new tab
 * - Separator for grouping tabs
 * - Collapsed state support
 *
 * Usage:
 *   <NavbarTab to="/dashboard" title="Dashboard" iconName="dashboard-icon" />
 *   <NavbarTab type="separator" />
 *   <NavbarTab to="https://external.com" external title="External" />
 */
import { clsx } from "clsx";
import type { FC, ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { Icon, type IconProps } from "../Icons/Icons";

export interface NavbarTabProps {
  /**
   * Internal or external link destination (URL or path).
   */
  to?: string;
  /**
   * Tab display text.
   */
  title?: string;
  /**
   * Icon name for tab (from design system).
   */
  iconName?: IconProps["name"];

  /**
   * When true, the tab is hidden while navbar is collapsed.
   */
  expandedOnly?: boolean;
  /**
   * If type is 'separator', renders a visual separator instead of a tab.
   * If type is 'heading', renders a section heading label.
   */
  type?: "separator" | "heading";
  /**
   * If true, renders as external link (<a>), otherwise uses NavLink.
   */
  external?: boolean;

  /**
   * Optional tag/badge component to render next to the label (e.g., NEW/BETA).
   */
  tag?: ReactNode;

  /**
   * Optional ReactNode override for the title text. When provided, renders
   * instead of `title` for visual display (title is still used for aria-label).
   */
  titleNode?: ReactNode;

  /**
   * Abbreviated title shown when the navbar is collapsed (for heading type).
   */
  collapsedTitle?: string;

  /**
   * Click handler — when provided (without `to`), renders a button instead of a NavLink.
   */
  onClick?: () => void;

  /**
   * Force active styling (useful for onClick tabs).
   */
  isActive?: boolean;

  /**
   * Explicit active override for link tabs. When provided, route matching is ignored.
   */
  forceIsActive?: boolean;

  /**
   * Whether this tab has child entries that are expanded/collapsed with navbar state.
   */
  hasExpandableChildren?: boolean;

  /**
   * Current navbar expansion state.
   */
  isNavbarExpanded?: boolean;
  /**
   * Show a small dot on the icon when navbar is collapsed (e.g. unread notifications).
   */
  showCollapsedDot?: boolean;
}

export const NavbarTab: FC<NavbarTabProps> = ({
  // Separator tab: renders a horizontal line for grouping
  to,
  title,
  titleNode,
  iconName,
  expandedOnly,
  type,
  external,
  tag,
  collapsedTitle,
  onClick,
  isActive,
  forceIsActive,
  hasExpandableChildren,
  isNavbarExpanded,
  showCollapsedDot,
}) => {
  if (type === "separator") {
    return (
      <li className="indigo-navbar-tab" aria-hidden>
        <div className="indigo-navbar-separator" />
      </li>
    );
  }

  if (type === "heading") {
    return (
      <li className="indigo-navbar-tab indigo-navbar-heading-tab" aria-hidden>
        <span className="indigo-navbar-heading collapsible">{title}</span>
        {collapsedTitle && (
          <span className="indigo-navbar-heading collapsed-only">
            {collapsedTitle}
          </span>
        )}
      </li>
    );
  }

  const renderIcon = () => {
    if (!iconName) return null;
    return (
      <span className="indigo-navbar-icon-wrapper">
        <Icon className="indigo-navbar-icon" name={iconName} size="m" />
        {showCollapsedDot && (
          <span
            className="indigo-navbar-tab-dot collapsed-only"
            aria-hidden="true"
          />
        )}
      </span>
    );
  };

  const renderLabel = () => (
    <div className="indigo-navbar-link-text-wrapper">
      <span className={"indigo-navbar-link-text collapsible truncate"}>
        {titleNode ?? title}
      </span>
      {tag && <span className="indigo-navbar-link-tag collapsible">{tag}</span>}
    </div>
  );

  const isVisuallyHiddenExpandedOnly = !!expandedOnly && !isNavbarExpanded;
  const sharedHiddenProps = isVisuallyHiddenExpandedOnly
    ? { "aria-hidden": true as const }
    : {};

  // Action button tab (no navigation, onClick handler)
  if (onClick && !to) {
    return (
      <li
        className={clsx("indigo-navbar-tab", expandedOnly && "expanded-only")}
        {...sharedHiddenProps}
      >
        <button
          type="button"
          className={clsx(
            "indigo-navbar-link",
            !iconName && "no-icon",
            (forceIsActive ?? isActive) && "is-active",
          )}
          onClick={onClick}
          aria-label={title}
          tabIndex={isVisuallyHiddenExpandedOnly ? -1 : 0}
          {...sharedHiddenProps}
        >
          {renderIcon()}
          {renderLabel()}
        </button>
      </li>
    );
  }

  if (external) {
    // External link: opens in new tab, uses <a>
    return (
      <li
        className={clsx("indigo-navbar-tab", expandedOnly && "expanded-only")}
        {...sharedHiddenProps}
      >
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx("indigo-navbar-link", !iconName && "no-icon")}
          tabIndex={isVisuallyHiddenExpandedOnly ? -1 : 0}
          {...sharedHiddenProps}
        >
          {renderIcon()}
          {renderLabel()}
        </a>
      </li>
    );
  }
  return (
    <li
      className={clsx("indigo-navbar-tab", expandedOnly && "expanded-only")}
      {...sharedHiddenProps}
    >
      {/* Internal link: uses NavLink for client-side navigation */}
      <NavLink
        to={to || "/"}
        className={({ isActive }) =>
          clsx(
            "indigo-navbar-link",
            !iconName && "no-icon",
            (forceIsActive ?? isActive) && "is-active",
          )
        }
        tabIndex={isVisuallyHiddenExpandedOnly ? -1 : 0}
        aria-label={title}
        aria-expanded={hasExpandableChildren ? !!isNavbarExpanded : undefined}
        {...sharedHiddenProps}
        end
      >
        {renderIcon()}
        {renderLabel()}
      </NavLink>
    </li>
  );
};

export default NavbarTab;
